import api from './api';

/** A pending tool action the assistant proposes; requires vendor confirmation. */
export interface AssistantPendingAction {
  actionKey: string;
  toolName: string;
  previewText: string;
  /** SPA path opening the manual form prefilled (document-upload proposals only). */
  editHref?: string;
}

/** A deep-link into a filtered finance page that backs the assistant's answer. */
export interface AssistantReplyLink {
  label: string;
  /** Same-origin relative SPA path (already validated server-side). */
  href: string;
}

export interface AssistantChatResponse {
  reply: string;
  pendingAction?: AssistantPendingAction;
  links?: AssistantReplyLink[];
}

export interface AssistantActionResult {
  success: boolean;
  message: string;
  /** Present when an edit produced a revised proposal to confirm. */
  pendingAction?: AssistantPendingAction;
}

/** Raw session turn shape returned by the backend (Gemini-style content parts). */
export interface AssistantSessionTurn {
  role: 'user' | 'model';
  parts: { text: string }[];
}

export interface AssistantSessionResponse {
  history: AssistantSessionTurn[];
}

/** An attached photo, base64-encoded with NO data-URI prefix. */
export interface AssistantImageAttachment {
  base64: string;
  mimeType: string;
}

/** Document-upload extras: raw PDF and/or browser-side OCR text. */
export interface AssistantDocumentExtras {
  pdfBase64?: string;
  clientExtractedText?: string;
}

export const assistantApi = {
  /**
   * Send a chat message and/or an attached photo/PDF; may return a
   * pendingAction awaiting confirmation. Attachments are sent inline as
   * base64 (not via the image gateway). `extras.clientExtractedText` is the
   * browser Tesseract output that lets the backend fingerprint statements
   * and invoices without an AI vision call.
   */
  chat: (message?: string, image?: AssistantImageAttachment, extras?: AssistantDocumentExtras) =>
    api.post<AssistantChatResponse>('/api/vendor/assistant/chat', {
      message: message || undefined,
      imageBase64: image?.base64,
      imageMimeType: image?.mimeType,
      pdfBase64: extras?.pdfBase64,
      clientExtractedText: extras?.clientExtractedText,
    }),

  /** Execute a proposed action. */
  confirmAction: (actionKey: string) =>
    api.post<AssistantActionResult>(`/api/vendor/assistant/actions/${actionKey}/confirm`),

  /** Discard a proposed action. */
  cancelAction: (actionKey: string) =>
    api.post<AssistantActionResult>(`/api/vendor/assistant/actions/${actionKey}/cancel`),

  /** Revise a proposed action with a new natural-language instruction. */
  editAction: (actionKey: string, instruction: string) =>
    api.post<AssistantActionResult>(`/api/vendor/assistant/actions/${actionKey}/edit`, {
      message: instruction,
    }),

  /** Rehydrate the conversation history for the current vendor session. */
  getSession: () => api.get<AssistantSessionResponse>('/api/vendor/assistant/session'),

  /** Clear the server-side conversation so the next message starts fresh. */
  clearSession: () => api.delete<{ success: boolean }>('/api/vendor/assistant/session'),
};
